import React, { useEffect, useState, useRef } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface MaintenanceCheckerProps {
  children: React.ReactNode;
}

const TIMEOUT_MS = 5000;

const MaintenanceChecker: React.FC<MaintenanceCheckerProps> = ({ children }) => {
  const location = useLocation();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    checkMaintenanceMode();

    // Subscribe to changes in maintenance mode
    if (supabase) {
      try {
        const channel = supabase
          .channel('site_settings_changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'site_settings',
              filter: 'key=eq.maintenance_mode'
            },
            (payload) => {
              if (payload.new && typeof payload.new === 'object' && 'value' in payload.new) {
                setIsMaintenanceMode(payload.new.value as boolean);
              }
            }
          )
          .subscribe();

        return () => {
          try {
            supabase.removeChannel(channel);
          } catch (err) {
            console.warn('Error removing channel:', err);
          }
        };
      } catch (err) {
        console.warn('Error setting up realtime channel:', err);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const checkMaintenanceMode = async () => {
    timeoutRef.current = setTimeout(() => {
      console.warn('Maintenance check timed out, defaulting to non-maintenance mode');
      setIsMaintenanceMode(false);
      setLoading(false);
    }, TIMEOUT_MS);

    try {
      if (!supabase) {
        setIsMaintenanceMode(false);
        setLoading(false);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        return;
      }

      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'maintenance_mode')
        .maybeSingle();

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      if (error) {
        console.error('Error checking maintenance mode:', error);
        setIsMaintenanceMode(false);
      } else {
        setIsMaintenanceMode(data?.value ?? false);
      }
    } catch (error) {
      console.error('Error checking maintenance mode:', error);
      setIsMaintenanceMode(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    } finally {
      setLoading(false);
    }
  };

  // Don't check maintenance for admin routes
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Show loading state
  if (loading && !isAdminRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Redirect to maintenance if enabled and not admin route
  if (isMaintenanceMode && !isAdminRoute) {
    return <Navigate to="/maintenance" replace />;
  }

  return <>{children}</>;
};

export default MaintenanceChecker;
